import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      // AuthContext will update state and App.jsx will redirect to /
    } catch (err) {
      const data = err.response?.data;
      if (data?.error === 'access_denied') {
        toast.error(data.message || 'Access denied. Contact hello@getstratapulse.com');
      } else {
        toast.error(data?.error || 'Invalid email or password');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-700 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl shadow-lg mb-3">
            <svg className="w-9 h-9 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white">StrataPulse</h1>
          <p className="text-blue-200 text-sm mt-1">BC Strata Management Platform</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-1">Sign In</h2>
          <p className="text-sm text-gray-500 mb-6">Welcome back — enter your credentials below.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                inputMode="email"
                className="input"
                value={form.email}
                onChange={set('email')}
                required
                placeholder="you@example.com"
                autoComplete="username"
              />
            </div>

            <div>
              <label className="label">Password</label>
              <input
                type="password"
                className="input"
                value={form.password}
                onChange={set('password')}
                required
                minLength={6}
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full mt-2 text-base"
              style={{ minHeight: 52 }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Signing in…
                </span>
              ) : 'Sign In'}
            </button>
          </form>

          {/* Invite-only notice */}
          <div className="mt-5 pt-4 border-t border-gray-100 text-center text-sm text-gray-500">
            <p>
              Don't have an account?{' '}
              <a
                href="mailto:hello@getstratapulse.com"
                className="text-blue-600 hover:underline font-medium"
              >
                Contact hello@getstratapulse.com
              </a>
            </p>
            <p className="text-xs text-gray-400 mt-1">
              StrataPulse is invite-only.
            </p>
          </div>

          <div className="mt-3 text-center text-sm text-gray-500">
            Are you a strata owner?{' '}
            <Link to="/owner/login" className="text-blue-600 hover:underline font-medium">
              Owner Portal →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
